import { Notify } from 'notiflix/build/notiflix-notify-aio';

import { setDisplayLoader } from '@/redux/app/appSlice';
import {
  setAccessToken,
  setAccessExpires,
  setLoginStatus,
  setSignupStatus,
  setForgotPwStatus,
  setResetPwStatus,
  setIsAuthorized,
  resetAuth,
} from '@/redux/auth/authSlice';
import { resetUser, setUserEmail, setUserId } from '@/redux/user/userSlice';
import { resetProfile } from '@/redux/profile/profileSlice';
import { resetAccounts } from '@/redux/accounts/accountsSlice';
import { resetDeposit } from '@/redux/deposit/depositSlice';
import { resetTransfer } from '@/redux/transfer/transferSlice';
import { resetWithdraw } from '@/redux/withdraw/withdrawSlice';
import { resetPresets } from '@/redux/presets/presetsSlice';
import { resetSupport } from '@/redux/support/supportSlice';
import { resetNotifications } from '@/redux/notifications/notificationsSlice';
import { resetPartner } from '@/redux/partner/partnerSlice';
import { resetUtm } from '@/redux/utm/utmSlice';
import { resetPamm } from '@/redux/pamm/pammSlice';

import { store } from '@/redux/store';

import { trackRegistration } from '@/utils/analytics';
import { getGaClientId } from '@/utils/ga4';

import authService from '@/services/authService';
import analyticService from '@/services/analyticService';

const signup = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    const data = await authService.signup(params);

    dispatch(setUserId(data.id));
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setSignupStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(e.response?.data?.message || 'Request Signup failure');
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const checkSignupOtp = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    await authService.checkSignupOtp(params);

    trackRegistration(params.client_id);

    dispatch(setSignupStatus('success'));
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setSignupStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request CheckSignupOtp failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const resendSignupOtp = email => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    await authService.resendSignupOtp(email);
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setSignupStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request ResendSignupOtp failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const login = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    const data = await authService.login(params);
    const { token, expires, client_id, email, requires_2fa } = data;

    // Если при логине пришел requires_2fa, значит сценарий 2-факторки с OTP-кодом
    if (requires_2fa) {
      dispatch(setUserId(client_id));
      dispatch(setUserEmail(email));

      return;
    }

    // Иначе, обычный сценарий логина
    if (token && expires) {
      dispatch(setAccessToken(token));
      dispatch(setAccessExpires(expires));

      dispatch(setIsAuthorized(true));
    }

    let gaClientId = null;

    try {
      gaClientId = await getGaClientId();
    } catch (error) {
      console.error('Failed to get GA4 client_id:', error);
    }

    await analyticService.sendGAClientId({
      ga_client_id: gaClientId,
      client_id,
    });
  } catch (e) {
    if (
      e.response?.status === 422 &&
      e.response?.data?.code === 'invalid_credentials' &&
      e.response?.data?.requires_recaptcha === false
    ) {
      dispatch(setLoginStatus('Invalid credentials'));
      return;
    }

    if (
      e.response?.status === 422 &&
      e.response?.data?.code === 'invalid_credentials' &&
      e.response?.data?.requires_recaptcha === true
    ) {
      dispatch(
        setLoginStatus('Invalid credentials. Confirm you are not a robot'),
      );
      return;
    }

    if (e.response?.status === 422) {
      dispatch(setLoginStatus(e?.response.data.message));
      return;
    }

    if (
      e.response?.status === 429 &&
      e.response?.data?.code === 'access_denied'
    ) {
      dispatch(setLoginStatus('Access denied'));
      return;
    }

    Notify.failure(e.response?.data?.message || 'Request Login failure');
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const checkLoginOtp = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    const data = await authService.checkLoginOtp(params);
    const { token, expires } = data;

    if (token && expires) {
      dispatch(setAccessToken(token));
      dispatch(setAccessExpires(expires));

      dispatch(setIsAuthorized(true));
    }
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setLoginStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request CheckLoginOtp failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const reset = () => async dispatch => {
  dispatch(resetSupport());
  dispatch(resetUtm());
  dispatch(resetPartner());
  dispatch(resetNotifications());
  dispatch(resetDeposit());
  dispatch(resetTransfer());
  dispatch(resetWithdraw());
  dispatch(resetPresets());
  dispatch(resetAccounts());
  dispatch(resetProfile());
  dispatch(resetPamm());
  dispatch(resetUser());
  dispatch(resetAuth());
};

const logout = () => async dispatch => {
  try {
    await authService.logout();
  } catch (e) {
    Notify.failure(e.response?.data?.message || 'Request Logout failure');
  } finally {
    dispatch(reset());
  }
};

const refreshToken = async () => {
  const { dispatch, getState } = store; // direct access to redux store

  const {
    auth: { accessToken },
  } = getState();

  if (!accessToken) return;

  try {
    const data = await authService.refreshToken();

    const { token, expires } = data;

    dispatch(setAccessToken(token));
    dispatch(setAccessExpires(expires));
  } catch (e) {
    // если куки нет или просрочена - сброс приложения
    if (e?.response?.status === 401) {
      dispatch(reset());
      return;
    }

    Notify.failure(
      e?.response?.data?.message || 'Request RefreshToken failure',
    );
  }
};

const forgotPassword = email => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    await authService.forgotPassword(email);

    dispatch(setUserEmail(email));
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setForgotPwStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request ForgotPassword failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const checkForgotOtp = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    await authService.checkForgotOtp(params);

    dispatch(setForgotPwStatus('success'));
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setForgotPwStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request CheckForgotOtp failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

const passwordReset = params => async dispatch => {
  try {
    dispatch(setDisplayLoader(true));

    await authService.passwordReset(params);

    dispatch(setResetPwStatus('success'));
  } catch (e) {
    if (e.response?.status === 422) {
      dispatch(setResetPwStatus(e.response?.data?.message));
      return;
    }

    Notify.failure(
      e.response?.data?.message || 'Request PasswordReset failure',
    );
  } finally {
    dispatch(setDisplayLoader(false));
  }
};

export default {
  signup,
  checkSignupOtp,
  resendSignupOtp,
  login,
  checkLoginOtp,
  logout,
  refreshToken,
  forgotPassword,
  checkForgotOtp,
  passwordReset,
  reset,
};
